import React, { Component, PureComponent } from 'react';
import { Input, Label, Button, Tooltip } from 'reactstrap';
import './Cards.css';
import Nav from '../../nav/Nav';
import Back from '../../Back';
import Card from '../../cards/Card';
import Champion from '../../cards/Champion';
import Deck from '../../cards/Deck';
import CardBox from '../../cards/CardBox';
import BasicButton from '../../buttons/BasicButton';
import MainButton from '../../buttons/MainButton';
import Library from '../../../scene/utility/Library';
import Lightbox from '../../utility/Lightbox';
import Loader from '../../utility/Loader';
import Img from '../../../StableImage';
import sorter from '../../cards/CollectionSorter';
import User from '../../../User';
import { read } from '../../../TextManager';

const PAGE_SIZE = 20;
const TYPE_ORDER = { attack: 0, special: 1, hex: 2, fusion: 3, "milk-bottle": 4 };
const SUBTYPE_ORDER = { engine: 1, augment: 2 };
const POOL_ORDER = { treasure: 0, shop: 1, menagerie: 2, extra: 3 };
const itemsorter = (a, b) =>
  (a.champion ? 100 : 0) - (b.champion ? 100 : 0) + a.name.localeCompare(b.name);
const normalizeSearch = text => (text || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const searchGroups = search => normalizeSearch(search)
  .replace(/\s*\/\s*/g, "/")
  .split(/\s+/)
  .map(group => group.split("/").filter(term => term))
  .filter(group => group.length);
const tokenKeysFor = text => {
  const keys = [];
  if (!text)
    return keys;
  const matches = text.matchAll(/\{([^{}]+)\}/g);
  for (const match of matches) {
    const key = match[1].split(/[:/]/)[0];
    if (key)
      keys.push(key);
  }
  return keys;
}
const searchableTextFor = (model, visited = new Set()) => {
  if (!model || visited.has(model.key))
    return "";
  visited.add(model.key);

  const ownText = [
    model.name,
    model.effect,
    model.passive?.effect
  ].filter(Boolean).join(" ");

  const tokenText = tokenKeysFor(ownText)
    .map(key => Library.getUpgradedCard(key) || Library.cards[key])
    .filter(Boolean)
    .map(token => searchableTextFor(token, visited))
    .join(" ");

  return `${ownText} ${tokenText}`;
}
const collectionSearchFilter = search => {
  const groups = searchGroups(search);
  if (groups.length === 0)
    return () => true;
  return (model, options = {}) => {
    const { known = true, kind = "card" } = options;
    if (!known && kind !== "card")
      return false;
    const text = normalizeSearch(known ? searchableTextFor(model) : model?.name);
    return groups.every(group => group.some(term => text.includes(term)));
  };
}

// Overlay for focused item/card/relic\ n
  class FocusOverlay extends PureComponent {
  render() {
    const { model, type, level, onClose, nav } = this.props;
    if (!model) return null;
    const isRelic = type.includes('relic');
    const isItem = type === 'item';
    return (
      <Lightbox open onClose={onClose}>
        <CardBox
          src={model}
          left={nav.left}
          right={nav.right}
          relic={isRelic}
          item={isItem}
          level={level}
          open={true}
          onClose={onClose}
        />
      </Lightbox>
    );
  }
}

// Pure grid of cards (all in view)
class CardGrid extends PureComponent {
  render() {
    const { levels, knows, onFocus } = this.props;
    return (
      <div className="card-list cards">
        {levels.map((list, lvl) => (
          <div key={lvl} className="level-list">
            {list.map(card => 
              knows('cards', card.key) ?
              <div
                key={card.key}
                className='listed-card'
                onClick={() => onFocus(card, 'card')}
              >
                <Card src={card} />
              </div> :
              <div
                key={card.key}
                className="listed-card locked"
                >
                <Card
                key={card.key}
                src={{
                  key: card.key,
                  name: card.name,
                  rarity: card.rarity,
                  img: "https://moa-pull.b-cdn.net/assets/ui/unknowncard.png"
                }}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }
}

// Pure list for relics
class RelicList extends PureComponent {
  render() {
    const { relics, knows, onFocus, pool } = this.props;
    return (
      <div className="card-list items">
        <div className="level-list">
          {relics.map(relic => {
            const type = pool + 'relic';
            return (
              <div
                key={relic.key}
                className={knows('relics', relic.key) ? 'listed-items' : 'listed-items locked'}
                onClick={() => (knows('relics', relic.key)) && onFocus(relic, type)}
              >
                <Img
                  src={knows('relics', relic.key) ? relic.img : '/images/unknown.png'}
                  alt={relic.name}
                  loading="lazy"
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}

// Pure list for potions
class PotionList extends PureComponent {
  render() {
    const { items, knows, onFocus } = this.props;
    return (
      <div className="card-list items">
        <div className="level-list">
          {items.map(item => (
            <div
              key={item.key}
              className={knows('potions', item.key) ? 'listed-items' : 'listed-items locked'}
              onClick={() => (knows('potions', item.key)) && onFocus(item, 'item')}
            >
              <Img
                src={knows('potions', item.key) ? item.img : '/images/unknown.png'}
                alt={item.name}
                loading="lazy"
              />
            </div>
          ))}
        </div>
      </div>
    );
  }
}

export default class Cards extends Component {
  state = {
    focus: null,
    focustype: null,
    tooltip: null,
    champion: User.champion,
    searchInput: '',
    collectionSearch: '',
    filter: { search: '', type: '', subtype: '', color: '', collectable: true, orderBy: 'key' },
    hiding: { decks: true, champions: true },
    page: { champions: 0 },
  };

  componentWillUnmount () {

    if (this.searchTimeout)
      clearTimeout(this.searchTimeout);
  }

  // Toggle focus overlay
  setFocus = (model, type) => {
    this.setState(prev => ({ focus: prev.focus === model ? null : model, focustype: type }));
  };

  closeOverlay = () => this.setState({ focus: null });

  updateSearch = event => {
    const searchInput = event.target.value;
    if (this.searchTimeout)
      clearTimeout(this.searchTimeout);
    this.setState({ searchInput });
    this.searchTimeout = setTimeout(() => this.setState({ collectionSearch: searchInput }), 500);
  };

  clearSearch = () => {
    if (this.searchTimeout)
      clearTimeout(this.searchTimeout);
    this.setState({ searchInput: '', collectionSearch: '' });
  };

  setFilter = values => {
    this.setState(prev => ({ filter: Object.assign({}, prev.filter, values) }));
  };

  render() {
    const { focus, focustype, champion, filter, searchInput, collectionSearch } = this.state;
    const knows = User.debugShowAllCollection ? () => true : User.knows.bind(User);
    const matchesCollectionSearch = collectionSearchFilter(collectionSearch);
    const mazePower = User.dataFor(champion, User.difficulty).devotion?.maze?.selected || 1;

    // Prepare card collection
    const collectionCards = User.debugShowAllCollection
      ? Object.values(Library.cards).filter(c => c.champion === champion && !c.token)
      : User.cards;
    const rawCards = collectionCards.map(c => Library.upgradeCard(c))//.filter(c => c.vanish);
    const cards = sorter.filter(rawCards, filter)
      .filter(c => !filter.type || knows('cards', c.key))
      .filter(c => matchesCollectionSearch(c, { kind: 'card', known: knows('cards', c.key) }));
    const byRarity = [
      cards.filter(c => c.rarity == null),
      ...Object.values(
        cards.filter(c => c.rarity != null).reduce((acc, c) => {
          (acc[c.rarity] = acc[c.rarity] || []).push(c);
          return acc;
        }, {})
      )
    ];
    const basicToOrder = key => key.includes("strike") ? 1 : (key.includes("defend") ? 2 : 3)
    const levels = byRarity.map((list, i) =>
      list.sort((a, b) =>
        i === 0
          ? basicToOrder(a.key) - basicToOrder(b.key)
          : 100 * ((TYPE_ORDER[a.type] ?? 99) - (TYPE_ORDER[b.type] ?? 99))
            + 10 * ((SUBTYPE_ORDER[Library.cardSubtypes(a)[0]] ?? 0) - (SUBTYPE_ORDER[Library.cardSubtypes(b)[0]] ?? 0))
            + (a.mana || 0) - (b.mana || 0)
            + (a.name < b.name ? -0.1 : 0.1)
      )
    );

    // Relics split
    const allRelics = Object.values(Library.relics)
      .map(r => Library.upgradeItem(r, mazePower))
      .filter(r => (!r.champion || r.champion === champion) && matchesCollectionSearch(r, { kind: 'relic', known: knows('relics', r.key) }));
    const championRelics = allRelics.filter(r => r.pool === 'treasure' && r.champion === champion).sort(itemsorter);
    const commonRelics = allRelics.filter(r => r.pool === 'treasure' && r.champion !== champion && r.rarity === 1).sort(itemsorter);
    const uncommonRelics = allRelics.filter(r => r.pool === 'treasure' && r.champion !== champion && r.rarity === 2).sort(itemsorter);
    const rareRelics = allRelics.filter(r => r.pool === 'treasure' && r.champion !== champion && r.rarity === 3).sort(itemsorter);
    const shopRelics = allRelics.filter(r => r.pool === 'shop').sort(itemsorter);
    const menagerieRelics = allRelics.filter(r => r.pool === 'menagerie').sort(itemsorter);
    const extraRelics = allRelics.filter(r => r.pool === 'extra').sort(itemsorter);
    const knownChampion = championRelics.filter(r => knows('relics', r.key));
    const knownCommonRelics = commonRelics.filter(r => knows('relics', r.key));
    const knownUncommonRelics = uncommonRelics.filter(r => knows('relics', r.key));
    const knownRareRelics = rareRelics.filter(r => knows('relics', r.key));
    const knownShop = shopRelics.filter(r => knows('relics', r.key));
    const knownMenagerie = menagerieRelics.filter(r => knows('relics', r.key));
    const knownExtra = extraRelics.filter(r => knows('relics', r.key));

    // Potions split
    const allItems = Object.values(Library.potions)
      .map(i => Library.upgradeItem(i, mazePower))
      .filter(
        i => (!i.champion || i.champion === champion || (Array.isArray(i.champion) && i.champion.includes(champion))) && matchesCollectionSearch(i, { kind: 'potion', known: knows('potions', i.key) })
      );
    const commonItems = allItems.filter(i => i.rarity === 1).sort(itemsorter);
    const uncommonItems = allItems.filter(i => i.rarity === 2).sort(itemsorter);
    const rareItems = allItems.filter(i => i.rarity === 3).sort(itemsorter);
    const knownCommonItems = commonItems.filter(i => knows('potions', i.key));
    const knownUncommonItems = uncommonItems.filter(i => knows('potions', i.key));
    const knownRareItems = rareItems.filter(i => knows('potions', i.key));

    // Compute navigation for FocusOverlay
    let nav = { left: undefined, right: undefined };
    if (focus) {
      if (focustype === 'card') {
        levels.forEach(list => {
          const knownList = list.filter(c => knows('cards', c.key));
          const idx = knownList.findIndex(c => c.key === focus.key);
          if (idx !== -1) {
            if (idx > 0) nav.left = () => this.setFocus(knownList[idx - 1], 'card');
            if (idx < knownList.length - 1) nav.right = () => this.setFocus(knownList[idx + 1], 'card');
          }
        });
      } else if (focustype === 'championrelic') {
        const idx = knownChampion.findIndex(r => r.key === focus.key);
        if (idx > 0) nav.left = () => this.setFocus(knownChampion[idx - 1], 'championrelic');
        if (idx < knownChampion.length - 1) nav.right = () => this.setFocus(knownChampion[idx + 1], 'championrelic');
      } else if (focustype === 'treasurerelic') {
        // determine on which rarity list
        let idx = knownCommonRelics.findIndex(r => r.key === focus.key);
        if (idx !== -1) {
          if (idx > 0) nav.left = () => this.setFocus(knownCommonRelics[idx - 1], 'treasurerelic');
          if (idx < knownCommonRelics.length - 1) nav.right = () => this.setFocus(knownCommonRelics[idx + 1], 'treasurerelic');
        } else {
          idx = knownUncommonRelics.findIndex(r => r.key === focus.key);
          if (idx !== -1) {
            if (idx > 0) nav.left = () => this.setFocus(knownUncommonRelics[idx - 1], 'treasurerelic');
            if (idx < knownUncommonRelics.length - 1) nav.right = () => this.setFocus(knownUncommonRelics[idx + 1], 'treasurerelic');
          } else {
            idx = knownRareRelics.findIndex(r => r.key === focus.key);
            if (idx > 0) nav.left = () => this.setFocus(knownRareRelics[idx - 1], 'treasurerelic');
            if (idx < knownRareRelics.length - 1) nav.right = () => this.setFocus(knownRareRelics[idx + 1], 'treasurerelic');
          }
        }
      } else if (focustype === 'shoprelic') {
        const idx = knownShop.findIndex(r => r.key === focus.key);
        if (idx > 0) nav.left = () => this.setFocus(knownShop[idx - 1], 'shoprelic');
        if (idx < knownShop.length - 1) nav.right = () => this.setFocus(knownShop[idx + 1], 'shoprelic');
      } else if (focustype === 'menagerierelic') {
        const idx = knownMenagerie.findIndex(r => r.key === focus.key);
        if (idx > 0) nav.left = () => this.setFocus(knownMenagerie[idx - 1], 'menagerierelic');
        if (idx < knownMenagerie.length - 1) nav.right = () => this.setFocus(knownMenagerie[idx + 1], 'menagerierelic');
      } else if (focustype === 'extrarelic') {
        const idx = knownExtra.findIndex(r => r.key === focus.key);
        if (idx > 0) nav.left = () => this.setFocus(knownExtra[idx - 1], 'extrarelic');
        if (idx < knownExtra.length - 1) nav.right = () => this.setFocus(knownExtra[idx + 1], 'extrarelic');
      } else if (focustype === 'item') {
        // determine on which rarity list
        let idx = knownCommonItems.findIndex(i => i.key === focus.key);
        if (idx !== -1) {
          if (idx > 0) nav.left = () => this.setFocus(knownCommonItems[idx - 1], 'item');
          if (idx < knownCommonItems.length - 1) nav.right = () => this.setFocus(knownCommonItems[idx + 1], 'item');
        } else {
          idx = knownUncommonItems.findIndex(i => i.key === focus.key);
          if (idx !== -1) {
            if (idx > 0) nav.left = () => this.setFocus(knownUncommonItems[idx - 1], 'item');
            if (idx < knownUncommonItems.length - 1) nav.right = () => this.setFocus(knownUncommonItems[idx + 1], 'item');
          } else {
            idx = knownRareItems.findIndex(i => i.key === focus.key);
            if (idx > 0) nav.left = () => this.setFocus(knownRareItems[idx - 1], 'item');
            if (idx < knownRareItems.length - 1) nav.right = () => this.setFocus(knownRareItems[idx + 1], 'item');
          }
        }
      }
    }

    return (
      <div className={`main-page cards-page champion-${champion}`}>
        {/* Focus overlay isolated */}
        <FocusOverlay
          model={focus}
          type={focustype}
          level={User.data.level}
          onClose={this.closeOverlay}
          nav={nav}
        />

        <Nav onChangeDifficulty={() => this.forceUpdate()} />
        <div className="main">
          <div className="cards-page-nav"></div>
          <div className="cards-page-list">
            <div className="collection-search-wrapper">
              <div className="collection-search-box">
                <Input
                  className="collection-search-input"
                  value={searchInput}
                  onChange={this.updateSearch}
                />
                <Input
                  type="select"
                  className="collection-filter-select"
                  value={filter.type}
                  onChange={event => this.setFilter({ type: event.target.value })}
                >
                  <option value="">Type</option>
                  <option value="attack">{read('cards/attack')}</option>
                  <option value="special">{read('cards/special')}</option>
                  <option value="other">other</option>
                </Input>
                <button
                  type="button"
                  className="collection-search-clear"
                  disabled={!searchInput}
                  onClick={this.clearSearch}
                >
                  Clear
                </button>
              </div>
            </div>
            {/* Cards Section */}
            <div className="cards-border">
              <div className="cards-border-left">{read('menu/cards')}<div className="cards-border-cardcount">({levels.reduce((sum, lvl) => sum + lvl.length, 0)})</div></div>
            </div>
            <CardGrid levels={levels} knows={knows} onFocus={this.setFocus} />

            {/* Relics Section */}
            <div className="cards-border">
              <div className="cards-border-left">{read('menu/relics')}<div className="cards-border-cardcount">({allRelics.length})</div></div>
            </div>
            <RelicList relics={championRelics} knows={knows} onFocus={this.setFocus} pool="champion" />
            <RelicList relics={commonRelics} knows={knows} onFocus={this.setFocus} pool="treasure" />
            <RelicList relics={uncommonRelics} knows={knows} onFocus={this.setFocus} pool="treasure" />
            <RelicList relics={rareRelics} knows={knows} onFocus={this.setFocus} pool="treasure" />
            <RelicList relics={shopRelics} knows={knows} onFocus={this.setFocus} pool="shop" />
            <RelicList relics={menagerieRelics} knows={knows} onFocus={this.setFocus} pool="menagerie" />
            <RelicList relics={extraRelics} knows={knows} onFocus={this.setFocus} pool="extra" />

            {/* Potions Section */}
            <div className="cards-border">
              <div className="cards-border-left">{read('menu/potions')}<div className="cards-border-cardcount">({allItems.length})</div></div>
            </div>
            <PotionList items={commonItems} knows={knows} onFocus={this.setFocus} />
            <PotionList items={uncommonItems} knows={knows} onFocus={this.setFocus} />
            <PotionList items={rareItems} knows={knows} onFocus={this.setFocus} />
          </div>

          <Back to="/play" />
        </div>
      </div>
    );
  }
}
